import { App, Form } from 'antd';
import { FormProviderProps } from 'antd/es/form/context';
import { useCallback, useEffect } from 'react';
import { generatePath, useNavigate } from 'react-router';

import { consumersActions } from '@store/consumers/consumers.slice';

import { RouteEnum } from '@enum/route.enum';
import { StepEnum } from '@enum/step.enum';
import { useCurrentApplicationParams } from '@hook/application/use-current-application-params.hook';
import { useMutation } from '@hook/common/use-mutation.hook';
import { useStep } from '@hook/common/use-step.hook';
import { useAddConsumerMutation } from '@hook/consumer/consumer.hooks';
import { useStoreDispatch, useStoreSelector } from '@hook/store/store.hooks';
import { isExist, isString } from '@util/data/is-exist';

export const useApplicationSetupConsumer = () => {
  const dispatch = useStoreDispatch();
  const step = useStep();
  const navigate = useNavigate();
  const { action, state } = useMutation(useAddConsumerMutation(), consumersActions, consumersActions.setConsumer);
  const [form] = Form.useForm();
  const { current } = useCurrentApplicationParams();
  const { message } = App.useApp();
  const error = useStoreSelector((state) => state.consumers.error);
  const onFinish: FormProviderProps['onFormFinish'] = (_, { values }) => {
    if (isExist(current) && isString(values.name)) {
      void action({ name: values.name, application: current.id });
    } else {
      form.resetFields();
      step.onChange(StepEnum.Consumer);
      navigate(generatePath(RouteEnum.ApplicationSetupNext.path, { applicationId: current.id }));
    }
  };

  const onBack = useCallback(
    () =>
      isExist(current) &&
      navigate(generatePath(RouteEnum.ApplicationSetupConnectors.path, { applicationId: current.id })),
    [current, navigate],
  );

  useEffect(() => {
    if (state.isSuccess) {
      dispatch(consumersActions.resetErrors());
      form.resetFields();
      step.onChange(StepEnum.Consumer);
    }
  }, [state.isSuccess, form]);

  useEffect(() => {
    if (state.isSuccess && step.step === StepEnum.Consumer) {
      message.success('Consumer was successfully added');
      navigate(generatePath(RouteEnum.ApplicationSetupNext.path, { applicationId: current.id }));
    }
  }, [step.step, state.isSuccess]);

  return {
    onBack,
    onFinish,
    form,
    error,
    isLoading: state.isLoading,
  };
};
